import type { TagView } from '@vizit/api';
import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { Trash01Icon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface DeleteTagDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The tag to delete.
   *
   * @required
   * @type {TagView | null}
   */
  tag: TagView | null;
  /**
   * Deletes the tag. Reject to keep the dialog open.
   *
   * @required
   * @type {(tag: TagView) => Promise<void>}
   */
  onDelete: (tag: TagView) => Promise<void>;
}

function describeUsage(imageCount: number) {
  if (imageCount === 0) return 'It is not used on any images.';
  return `It will be removed from ${imageCount.toLocaleString()} ${imageCount === 1 ? 'image' : 'images'}.`;
}

/**
 * Confirmation dialog shown before a tag is deleted, with the number of images that use it.
 *
 * @param props - The props for the DeleteTagDialog component.
 */
function DeleteTagDialog(props: DeleteTagDialogProps) {
  const { isOpen = false, onOpenChange, onDelete, tag, ...other } = props;
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleOnDelete = React.useCallback(async () => {
    if (!tag) return;

    setIsDeleting(true);
    try {
      await onDelete(tag);
      onOpenChange?.(false);
    } catch {
      // The caller reports the failure; the dialog stays open so the delete can be retried.
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete, onOpenChange, tag]);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-4">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={Trash01Icon}>
            <Dialog.Title showEllipsis={true}>{tag ? `Delete "${tag.name}"` : 'Delete tag'}</Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              {tag ? `${describeUsage(tag.image_count)} ` : ''}This can't be undone.
            </Dialog.Subtitle>
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={() => onOpenChange?.(false)}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Danger}
              onPress={handleOnDelete}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { DeleteTagDialog };
export type { DeleteTagDialogProps };
